import ShareWithMe from './ShareWithMe';
import ShareByMe from './ShareByMe';
import { useUI } from 'store/UI/selectors';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetRedirectUrlForBook } from 'containers/hooks';
import useBook from 'components/hooks/books/useBook';
import { useCurrentUser } from 'store/users/selectors';
import { useOneStoryWithAi } from 'store/StoryAiCofig/selectors';
import moment from 'moment-timezone';
import { handleListShareAndReciveBooks } from 'store/books/thunks';
import { useDispatch } from 'react-redux';
import { uniqBy } from 'lodash';
import { useWorkSheetSelector } from 'store/worksheet/selectors';
import { handleListWorkSheet } from 'store/worksheet/thunks';
import SentryWrap from 'components/Sentry';

const SuperLessonDiffList = ({
  shareWithMe,
  setShareWithMe,
  shareByMe,
  setShareByMe,
  chapter,
  from = '',
}) => {
  const { listViewSharedAndReceiveBooks } = useUI();
  const navigate = useNavigate();
  const { redirectToBook } = useGetRedirectUrlForBook();
  const superLesson = listViewSharedAndReceiveBooks?.superLesson || {};
  const differentiation = listViewSharedAndReceiveBooks?.differentiationLesson || {};
  const sentenceStarter = listViewSharedAndReceiveBooks?.sentenceStarterLesson || {};
  const currentUser = useCurrentUser();
  const { activities, quizzes } = useBook();
  const getStoryWithAi = useOneStoryWithAi();
  const listWorkSheet = useWorkSheetSelector();
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        dispatch(
          handleListShareAndReciveBooks({ currentUserId: currentUser?.id, bookId: chapter }),
        ),
        dispatch(
          handleListWorkSheet(chapter, currentUser?.role, currentUser?.id, 100, 0, true),
        ),
      ]);
    } catch (error) {
      console.log(error, 123);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (shareByMe || shareWithMe) {
      fetchData();
    }
  }, [chapter, shareByMe, shareWithMe]);

  const filterSharedByMeAssignments = useMemo(() => {
    return activities?.filter((activity) => activity?.chapter === chapter) || [];
  }, [activities]);

  const fitlerSharedByMeQuizzes = useMemo(() => {
    return quizzes?.filter((quiz) => quiz?.chapter === chapter) || [];
  }, [quizzes]);

  const filterSharedByMeWorksheets = useMemo(() => {
    return listWorkSheet?.filter((worksheet) => worksheet?.book_id === chapter) || [];
  }, [listWorkSheet, chapter]);

  const sharedByMeAssignments = useMemo(() => {
    const data = [...filterSharedByMeAssignments, ...fitlerSharedByMeQuizzes];
    const getAllSharedData = data.filter(
      (item) => item?.share_list !== null && item?.share_list.length > 0 && item?.share_by !== null,
    );

    const sharedByMe = getAllSharedData?.filter(
      (item) => item?.share_by === currentUser?.id && !item?.share_list?.includes(currentUser?.id),
    );

    if (sharedByMe?.length > 0) {
      return sharedByMe?.map((res) => {
        return {
          bookId: res?.chapter,
          pageId: res?.page,
          userId: res?.createdBy,
          access_by: res?.share_list,
          created_at: res?.modifiedOn || res?.created_at || new Date().toISOString(),
          name: res?.displayName,
          shared_time: res?.shared_time
        };
      });
    } else {
      return [];
    }
  }, [filterSharedByMeAssignments, fitlerSharedByMeQuizzes]);

  const sharedWithMeAssignments = useMemo(() => {
    const data = [...filterSharedByMeAssignments, ...fitlerSharedByMeQuizzes];
    const getAllSharedData = data.filter(
      (item) => item?.share_list !== null && item?.share_list.length > 0 && item?.share_by !== null,
    );

    const sharedWithMe = getAllSharedData?.filter(
      (item) => item?.share_by !== currentUser?.id && item?.share_list?.includes(currentUser?.id),
    );

    if (sharedWithMe?.length > 0) {
      return sharedWithMe?.map((res) => {
        return {
          bookId: res?.chapter,
          pageId: res?.page,
          userId: res?.createdBy,
          access_by: res?.share_list,
          created_at: res?.modifiedOn || res?.created_at || new Date().toISOString(),
          name: res?.displayName,
          shared_time: res?.shared_time
        };
      });
    } else {
      return [];
    }
  }, [filterSharedByMeAssignments, fitlerSharedByMeQuizzes, currentUser]);

  const sharedByMeWorksheets = useMemo(() => {
    const getAllSharedData = filterSharedByMeWorksheets?.filter((item) => {
      const shareList = Array.isArray(item?.share_list) 
        ? item.share_list 
        : typeof item?.share_list === 'string' 
          ? JSON.parse(item.share_list || '[]') 
          : [];
      return shareList.length > 0 && item?.share_by !== null;
    });

    const sharedByMe = getAllSharedData?.filter((item) => {
      const shareList = Array.isArray(item?.share_list) 
        ? item.share_list 
        : typeof item?.share_list === 'string' 
          ? JSON.parse(item.share_list || '[]') 
          : [];
      return item?.share_by === currentUser?.id && !shareList.includes(currentUser?.id);
    });

    if (sharedByMe?.length > 0) {
      return sharedByMe?.map((res) => {
        const shareList = Array.isArray(res?.share_list) 
          ? res.share_list 
          : typeof res?.share_list === 'string' 
            ? JSON.parse(res.share_list || '[]') 
            : [];
        return {
          bookId: res?.book_id,
          pageId: res?.page_id,
          userId: res?.user_id,
          access_by: shareList,
          created_at: res?.created_at || new Date().toISOString(),
          name: res?.display_name,
          shared_time: res?.shared_time
        };
      });
    } else {
      return [];
    }
  }, [filterSharedByMeWorksheets, currentUser]);

  const sharedWithMeWorksheets = useMemo(() => {
    const getAllSharedData = filterSharedByMeWorksheets?.filter((item) => {
      const shareList = Array.isArray(item?.share_list) 
        ? item.share_list 
        : typeof item?.share_list === 'string' 
          ? JSON.parse(item.share_list || '[]') 
          : [];
      return shareList.length > 0 && item?.share_by !== null;
    });

    const sharedWithMe = getAllSharedData?.filter((item) => {
      const shareList = Array.isArray(item?.share_list) 
        ? item.share_list 
        : typeof item?.share_list === 'string' 
          ? JSON.parse(item.share_list || '[]') 
          : [];
      return item?.share_by !== currentUser?.id && shareList.includes(currentUser?.id);
    });

    if (sharedWithMe?.length > 0) {
      return sharedWithMe?.map((res) => {
        const shareList = Array.isArray(res?.share_list) 
          ? res.share_list 
          : typeof res?.share_list === 'string' 
            ? JSON.parse(res.share_list || '[]') 
            : [];
        return {
          bookId: res?.book_id,
          pageId: res?.page_id,
          userId: res?.user_id,
          access_by: shareList,
          created_at: res?.created_at || new Date().toISOString(),
          name: res?.display_name,
          shared_time: res?.shared_time
        };
      });
    } else {
      return [];
    }
  }, [filterSharedByMeWorksheets, currentUser]);

  const sharedByMeStoryWithAi = useMemo(() => {
    try {
      const filterShared =
        getStoryWithAi.filter(
          (story) => story?.shared_with !== null && story.user_id === currentUser?.id,
        ) || [];

      const filterSharedByMe =
        filterShared.filter(
          (story) =>
            !story?.shared_with?.includes(currentUser?.id),
        ) || [];

      if (filterSharedByMe?.length > 0) {
        const data =  filterSharedByMe.map((story) => {
          return {
            bookId: story?.chapter,
            pageId: story?.page,
            userId: story?.user_id,
            access_by: story?.shared_with,
            created_at: story?.created_at || moment().format('MMMM Do, YYYY'), // current
            name: story?.story_title,
            shared_time: story?.shared_time
          };
        });
        const uniqByStoryTitle = uniqBy(data, 'name');
        return uniqByStoryTitle;
      } else {
        return [];
      }
    } catch (error) {
      console.log(error, 123);
    }
  }, [getStoryWithAi]);

  const sharedWithMeStoryWithAi = useMemo(() => {
    try {
      const filterShared = getStoryWithAi.filter((story) => story?.shared_from !== null) || [];

      const filterSharedWithMe =
        filterShared.filter((story) => Object.keys(story?.shared_from || {}).length > 0) || [];

      if (filterSharedWithMe?.length > 0) {
        const data = filterSharedWithMe.map((story) => {
          return {
            bookId: story?.chapter,
            pageId: story?.page,
            userId: story?.shared_from?.shared_by?.id,
            access_by: [story?.shared_from?.shared_by?.id],
            created_at: story?.created_at || '',
            name: story?.story_title,
            shared_time: story?.shared_time
          };
        });
        const uniqByStoryTitle = uniqBy(data, 'name');
        return uniqByStoryTitle;
      } else {
        return [];
      }
    } catch (error) {
      console.log(error, 123);
    }
  }, [getStoryWithAi]);

  const data = useMemo(() => {
    if (loading) {
      return [];
    }
    try {
      if (shareByMe) {
        const result = [
          ...(superLesson?.sharedByMe || []),
          ...(differentiation?.differnationSharedByMe || []),
          ...(sentenceStarter?.sentenceStarterSharedByMe || []),
          ...(sharedByMeAssignments || []),
          ...(sharedByMeStoryWithAi || []),
          ...(sharedByMeWorksheets || []),
        ];
        return result.sort((a, b) => Number(a.pageId) - Number(b.pageId));
      } else if (shareWithMe) {
        const result = [
          ...(superLesson?.receivedByMe || []),
          ...(differentiation?.differnationReceivedByMe || []),
          ...(sentenceStarter?.sentenceStarterReceivedByMe || []),
          ...(sharedWithMeAssignments || []),
          ...(sharedWithMeStoryWithAi || []),
          ...(sharedWithMeWorksheets || []),
        ];
        return result.sort((a, b) => Number(a.pageId) - Number(b.pageId));
      }
    } catch (error) {
      console.log(error, 123);
    }
  }, [
    loading,
    superLesson,
    differentiation,
    sentenceStarter,
    sharedByMeStoryWithAi,
    sharedWithMeStoryWithAi,
    sharedByMeAssignments,
    sharedWithMeAssignments,
    sharedByMeWorksheets,
    sharedWithMeWorksheets,
    shareByMe,
    shareWithMe,
  ]);

  const redirectPage = (bookId, pageId, noteId = null) => {
    try {
      if (from === 'podcast') {
        return;
      }
      let url = redirectToBook({ chapter: bookId, page: pageId });
      
      if (noteId) {
        url = url.includes('?') 
          ? `${url}&noteId=${noteId}`
          : `${url}?noteId=${noteId}`;
      }
      navigate(url);
    } catch (error) {
      console.log(error, 123);
    }
  };

  return (
    <SentryWrap>
    <div>
      <ShareWithMe
        shareWithMe={shareWithMe}
        setShareWithMe={setShareWithMe}
        data={data}
        redirectPage={redirectPage}
        from={from}
        loading={loading}
      />
      <ShareByMe
        shareByMe={shareByMe}
        setShareByMe={setShareByMe}
        data={data}
        redirectPage={redirectPage}
        from={from}
        loading={loading}
      />
    </div>
    </SentryWrap>
  );
};
export default SuperLessonDiffList;
